import { useEffect, useMemo, useState } from 'react'
import type { JSX } from 'react'
import { describeError } from '../../api/errors'
import type { Assignment, Game, GameFull, RoomView } from '../../api/rooms'
import { games as gamesApi, rooms as roomsApi, sessions as sessionsApi } from '../../api/rooms'
import { formatTime } from '../../lib/format'
import { navigate } from '../../lib/router'
import { pushToast } from '../../lib/toast'

export type RoomLobbyProps = {
  view: RoomView
  isAdmin: boolean
  onReload: () => void
  onStart: (game: GameFull, assignments: Assignment[]) => Promise<void>
}

/**
 * Лобби: кто в комнате, что играем, кто какую роль берёт.
 * Участник видит то же самое, но без управления — ему важно понимать,
 * чего он ждёт и какая роль ему досталась.
 */
export function RoomLobby({ view, isAdmin, onReload, onStart }: RoomLobbyProps): JSX.Element {
  const [catalogue, setCatalogue] = useState<Game[]>([])
  const [assign, setAssign] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [copied, setCopied] = useState(false)

  const game = view.game
  const link = view.link

  useEffect(() => {
    if (!isAdmin) return
    void gamesApi
      .list()
      .then(setCatalogue)
      .catch((err: unknown) => pushToast('error', describeError(err)))
  }, [isAdmin])

  // Раздача по умолчанию: роли расходятся по кругу между участниками, чтобы
  // ведущему не пришлось кликать в самом частом случае.
  useEffect(() => {
    if (!game) return
    setAssign((prev) => {
      if (Object.keys(prev).length > 0) return prev
      const next: Record<string, string> = {}
      game.roles.forEach((role, i) => {
        const person = view.participants[i % Math.max(1, view.participants.length)]
        if (person) next[role.id] = person.id
      })
      return next
    })
  }, [game, view.participants])

  const assignments = useMemo<Assignment[]>(
    () =>
      Object.entries(assign)
        .filter(([, participantId]) => participantId)
        .map(([roleId, participantId]) => ({ roleId, participantId })),
    [assign],
  )

  const myRoles = useMemo(() => {
    if (!game || !view.me) return []
    return game.roles.filter((role) => assign[role.id] === view.me?.id)
  }, [game, assign, view.me])

  const copyLink = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(link)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      pushToast('info', link)
    }
  }

  return (
    <div className="page room-page">
      <header className="room-head">
        <div>
          <h1>{view.room.title}</h1>
          <p className="faint">
            Код комнаты <span className="mono code-badge">{view.room.code}</span>
          </p>
        </div>
        <span className="spacer" />
        <button type="button" onClick={() => void copyLink()}>
          {copied ? 'Ссылка скопирована' : 'Скопировать ссылку'}
        </button>
        {isAdmin ? (
          <button
            type="button"
            className="ghost danger"
            onClick={() => {
              if (!confirm('Закрыть комнату? Все записи этой комнаты будут удалены.')) return
              void roomsApi
                .remove(view.room.id)
                .then(() => navigate({ name: 'games' }))
                .catch((err: unknown) => pushToast('error', describeError(err)))
            }}
          >
            Закрыть комнату
          </button>
        ) : null}
      </header>

      <div className="room-columns">
        <section className="block">
          <h2>Участники ({view.participants.length})</h2>
          <ul className="participant-list">
            {view.participants.map((p) => (
              <li key={p.id}>
                <span className={p.online ? 'dot online' : 'dot'} />
                <span>{p.name}</span>
                {p.isAdmin ? <span className="badge">ведущий</span> : null}
                {view.me?.id === p.id ? <span className="badge">вы</span> : null}
                <span className="spacer" />
                {isAdmin && !p.isAdmin ? (
                  <button
                    type="button"
                    className="ghost danger small"
                    onClick={() => {
                      void roomsApi
                        .kick(view.room.id, p.id)
                        .then(onReload)
                        .catch((err: unknown) => pushToast('error', describeError(err)))
                    }}
                  >
                    Убрать
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
          {view.participants.length < 2 ? (
            <p className="faint">Отправьте ссылку друзьям — они войдут, назвав своё имя.</p>
          ) : null}
        </section>

        <section className="block">
          <h2>Что озвучиваем</h2>
          {isAdmin ? (
            <select
              value={game?.game.id ?? ''}
              onChange={(e) => {
                setAssign({})
                void roomsApi
                  .setGame(view.room.id, e.target.value)
                  .then(onReload)
                  .catch((err: unknown) => pushToast('error', describeError(err)))
              }}
            >
              <option value="">— выберите озвучку —</option>
              {catalogue.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.title} · {formatTime(g.duration)} · ролей {g.rolesCount}
                </option>
              ))}
            </select>
          ) : game ? (
            <p>
              <strong>{game.game.title}</strong>{' '}
              <span className="faint">
                {formatTime(game.game.duration)} · реплик {game.lines.length}
              </span>
            </p>
          ) : (
            <p className="faint">Ведущий ещё не выбрал сцену.</p>
          )}

          {game ? (
            <>
              <h3>Роли</h3>
              <ul className="role-assign">
                {game.roles.map((role) => {
                  const count = game.lines.filter((l) => l.roleId === role.id).length
                  return (
                    <li key={role.id}>
                      <span className="role-chip" style={{ background: role.color }} />
                      <span className="role-name">{role.name}</span>
                      <span className="faint">{count} реплик</span>
                      <span className="spacer" />
                      {isAdmin ? (
                        <select
                          value={assign[role.id] ?? ''}
                          onChange={(e) => setAssign((prev) => ({ ...prev, [role.id]: e.target.value }))}
                        >
                          <option value="">— никто —</option>
                          {view.participants.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span>
                          {view.participants.find((p) => p.id === assign[role.id])?.name ?? '— не назначено —'}
                        </span>
                      )}
                    </li>
                  )
                })}
              </ul>

              {!isAdmin ? (
                <p className="faint">
                  {myRoles.length > 0
                    ? `Ваши роли: ${myRoles.map((r) => r.name).join(', ')}. Ждём, когда ведущий начнёт.`
                    : 'Ждём, когда ведущий раздаст роли и начнёт игру.'}
                </p>
              ) : (
                <button
                  type="button"
                  className="primary big"
                  disabled={busy || assignments.length === 0}
                  onClick={() => {
                    if (!game) return
                    setBusy(true)
                    void onStart(game, assignments).finally(() => setBusy(false))
                  }}
                >
                  {assignments.length === 0 ? 'Назначьте хотя бы одну роль' : 'Начать игру'}
                </button>
              )}
            </>
          ) : null}
        </section>
      </div>

      {view.sessions.filter((s) => s.state === 'done').length > 0 ? (
        <section className="block">
          <h2>Записи этой комнаты</h2>
          <ul className="session-list">
            {view.sessions
              .filter((s) => s.state === 'done')
              .map((s) => (
                <li key={s.id}>
                  <video src={sessionsApi.resultUrl(s.id)} controls preload="metadata" />
                  <a href={sessionsApi.resultUrl(s.id)} download>
                    Скачать
                  </a>
                </li>
              ))}
          </ul>
        </section>
      ) : null}
    </div>
  )
}
